export type ShortcutAction =
  | 'tool.select'
  | 'tool.character'
  | 'tool.facing'
  | 'tool.path'
  | 'tool.camera'
  | 'file.load'
  | 'edit.undo'
  | 'edit.redo'
  | 'edit.deleteSelected'
  | 'edit.clearPath'
  | 'export.png'
  | 'copy.prompt'
  | 'copy.json'
  | 'view.cancel';

export type ShortcutMap = Record<ShortcutAction, string[]>;

export type ShortcutDefinition = {
  action: ShortcutAction;
  label: string;
  detail?: string;
  defaultCombos: string[];
};

export type ShortcutGroupDefinition = {
  title: string;
  definitions: ShortcutDefinition[];
};

export type ShortcutHelpItem = {
  label: string;
  keys: string[];
  detail?: string;
};

export type ShortcutHelpGroup = {
  title: string;
  items: ShortcutHelpItem[];
};

export const SHORTCUT_STORAGE_KEY = 'shotmap-studio-en-shortcuts-v1';

export const shortcutGroups: ShortcutGroupDefinition[] = [
  {
    title: 'Tool Switching',
    definitions: [
      { action: 'tool.select', label: 'Select / Move', defaultCombos: ['V', '1'] },
      { action: 'tool.character', label: 'Add Character', defaultCombos: ['R', '2'] },
      { action: 'tool.facing', label: 'Set Facing', defaultCombos: ['F', '3'] },
      { action: 'tool.path', label: 'Add Path', defaultCombos: ['P', '4'] },
      { action: 'tool.camera', label: 'Add Camera', defaultCombos: ['C', '5'] }
    ]
  },
  {
    title: 'Edit & Export',
    definitions: [
      { action: 'file.load', label: 'Upload Background', defaultCombos: ['L'] },
      { action: 'edit.undo', label: 'Undo', defaultCombos: ['Mod+Z'] },
      { action: 'edit.redo', label: 'Redo', defaultCombos: ['Mod+Shift+Z', 'Mod+Y'] },
      { action: 'edit.deleteSelected', label: 'Delete Selection', defaultCombos: ['Delete', 'Backspace'] },
      { action: 'edit.clearPath', label: 'Clear Character Path', detail: 'Requires a selected character', defaultCombos: ['X'] },
      { action: 'export.png', label: 'Export PNG', defaultCombos: ['E'] },
      { action: 'copy.prompt', label: 'Copy Prompt', defaultCombos: ['Shift+P'] },
      { action: 'copy.json', label: 'Copy JSON', defaultCombos: ['Shift+J'] },
      { action: 'view.cancel', label: 'Cancel Selection', defaultCombos: ['Escape'] }
    ]
  }
];

export const fixedShortcutHelpGroup: ShortcutHelpGroup = {
  title: 'Fine Adjustment',
  items: [
    { label: 'Move selected object', keys: ['Arrow keys'] },
    { label: 'Fast move', keys: ['Shift + Arrow keys'] },
    { label: 'Micro move', keys: ['Alt + Arrow keys'] }
  ]
};

export const shortcutDefinitions = shortcutGroups.flatMap((group) => group.definitions);

const modifierKeys = new Set(['Alt', 'Control', 'Meta', 'Shift']);

export function createDefaultShortcutMap(): ShortcutMap {
  return shortcutDefinitions.reduce((result, definition) => {
    result[definition.action] = [...definition.defaultCombos];
    return result;
  }, {} as ShortcutMap);
}

export function normalizeShortcutMap(value: unknown): ShortcutMap {
  const defaults = createDefaultShortcutMap();
  if (!value || typeof value !== 'object') return defaults;
  const record = value as Partial<Record<ShortcutAction, unknown>>;
  shortcutDefinitions.forEach((definition) => {
    const saved = record[definition.action];
    if (!Array.isArray(saved)) return;
    const combos = saved.filter((combo): combo is string => typeof combo === 'string' && combo.trim().length > 0);
    if (combos.length) defaults[definition.action] = Array.from(new Set(combos));
  });
  return defaults;
}

export function readShortcutMap(): ShortcutMap {
  try {
    const saved = window.localStorage.getItem(SHORTCUT_STORAGE_KEY);
    if (saved) return normalizeShortcutMap(JSON.parse(saved));
  } catch {
    // Local storage may be unavailable in private contexts.
  }
  return createDefaultShortcutMap();
}

export function saveShortcutMap(shortcuts: ShortcutMap) {
  window.localStorage.setItem(SHORTCUT_STORAGE_KEY, JSON.stringify(shortcuts));
}

function keyName(key: string) {
  if (key === ' ') return 'Space';
  if (key === 'Esc') return 'Escape';
  if (key.length === 1) return key.toUpperCase();
  return key;
}

export function eventToShortcutCombo(event: KeyboardEvent): string | null {
  if (modifierKeys.has(event.key)) return null;
  const parts: string[] = [];
  if (event.metaKey || event.ctrlKey) parts.push('Mod');
  if (event.shiftKey) parts.push('Shift');
  if (event.altKey) parts.push('Alt');
  parts.push(keyName(event.key));
  return parts.join('+');
}

export function shortcutActionForEvent(event: KeyboardEvent, shortcuts: ShortcutMap): ShortcutAction | null {
  const combo = eventToShortcutCombo(event);
  if (!combo) return null;
  return shortcutDefinitions.find((definition) => shortcuts[definition.action]?.includes(combo))?.action ?? null;
}

export function findShortcutConflict(shortcuts: ShortcutMap, action: ShortcutAction, combo: string) {
  return shortcutDefinitions.find((definition) => definition.action !== action && shortcuts[definition.action]?.includes(combo)) ?? null;
}

export function formatShortcutCombo(combo: string) {
  return combo
    .split('+')
    .map((part) => {
      if (part === 'Mod') return 'Cmd/Ctrl';
      if (part === 'Escape') return 'Esc';
      return part;
    })
    .join(' + ');
}

export function buildShortcutHelpGroups(shortcuts: ShortcutMap): ShortcutHelpGroup[] {
  return [
    ...shortcutGroups.map((group) => ({
      title: group.title,
      items: group.definitions.map((definition) => ({
        label: definition.label,
        detail: definition.detail,
        keys: shortcuts[definition.action].map(formatShortcutCombo)
      }))
    })),
    fixedShortcutHelpGroup
  ];
}

export function isEditableShortcutTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  const editable = target.closest('input, textarea, select, [contenteditable="true"]');
  return Boolean(editable);
}
